import { useForm } from 'react-hook-form';
import { ITicketFormData, PaymentStatus } from '@/interfaces/tickets/ITicket';

interface TicketFormProps {
  onSubmit: (data: ITicketFormData) => void;
  isLoading?: boolean;
  initialData?: Partial<ITicketFormData>;
}

export const TicketForm = ({ onSubmit, isLoading, initialData }: TicketFormProps) => {
  const { register, handleSubmit, formState: { errors } } = useForm<ITicketFormData>({
    defaultValues: initialData
  });

  const validateFile = (value: any) => {
    if (initialData) return true;
    
    if (!value?.length) return 'File is required';

    const file = value[0];
    
    const acceptedMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/webp'
    ];

    const acceptedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];

    if (acceptedMimeTypes.includes(file.type)) return true;

    const fileName = file.name.toLowerCase();
    const hasValidExtension = acceptedExtensions.some(ext => fileName.endsWith(ext));
    
    if (hasValidExtension) return true;

    return 'File must be a PDF or image (jpg, png, jpeg)';
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label className="block text-[#0F9015] text-xl mb-2 font-heading">
          File (PDF or Image)
          <input
            type="file"
            {...register('file', { 
              validate: validateFile
            })}
            className="mt-2 block w-full text-lg border border-gray-200 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-lg file:bg-[#C9FD05] file:text-black hover:file:bg-opacity-90 font-body"
            accept=".pdf,.jpg,.jpeg,.png"
          />
        </label>
        {errors.file && (
          <p className="mt-1 text-red-600 font-body">{errors.file.message}</p>
        )}
      </div>

      <div>
        <label className="block text-[#0F9015] text-xl mb-2 font-heading">
          Event Name
          <input
            type="text"
            {...register('eventName', { required: 'Event name is required' })}
            className="mt-2 block w-full text-lg text-[#084509] border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0F9015] font-body"
          />
        </label>
        {errors.eventName && (
          <p className="mt-1 text-red-600 font-body">{errors.eventName.message}</p>
        )}
      </div>

      <div>
        <label className="block text-[#0F9015] text-xl mb-2 font-heading">
          Status
          <select
            {...register('payment_status', { required: 'Status is required' })}
            className="mt-2 block w-full text-lg text-[#084509] border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0F9015] font-body"
          >
            <option value={PaymentStatus.PENDING}>Pending</option>
            <option value={PaymentStatus.PAID}>Paid</option>
          </select>
        </label>
        {errors.payment_status && (
          <p className="mt-1 text-red-600 font-body">{errors.payment_status.message}</p>
        )}
      </div>

      <div>
        <label className="block text-[#0F9015] text-xl mb-2 font-heading">
          Recipient Email
          <input
            type="email"
            {...register('email', { 
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email'
              }
            })}
            className="mt-2 block w-full text-lg text-[#084509] border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0F9015] font-body"
          />
        </label>
        {errors.email && (
          <p className="mt-1 text-red-600 font-body">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label className="block text-[#0F9015] text-xl mb-2 font-heading">
          Issuer Note
          <textarea
            {...register('emmiter_note')}
            className="mt-2 block w-full text-lg text-[#084509] border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0F9015] font-body"
            rows={4}
          />
        </label>
      </div>

      <div>
        <label className="block text-[#0F9015] text-xl mb-2 font-heading">
          Recipient Note
          <textarea
            {...register('recipient_note')}
            className="mt-2 block w-full text-lg text-[#084509] border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0F9015] font-body"
            rows={4}
          />
        </label>
      </div>

      <div>
        <label className="block text-[#0F9015] text-xl mb-2 font-heading">
          Price
          <input
            type="number"
            {...register('price', { 
              required: 'Price is required',
              min: { value: 0, message: 'Price must be greater than 0' }
            })}
            className="mt-2 block w-full text-lg text-[#084509] border border-gray-200 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#0F9015] font-body"
          />
        </label>
        {errors.price && (
          <p className="mt-1 text-red-600 font-body">{errors.price.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-[#C9FD05] hover:opacity-90 transition-opacity px-6 py-3 rounded-lg text-lg disabled:opacity-50 flex items-center justify-center font-body"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black mr-2"></div>
            Saving...
          </>
        ) : (
          'Save Ticket'
        )}
      </button>
    </form>
  );
}; 